import { useEffect } from 'react';

/**
 * Router Helper Functions
 * 
 * Utility functions for user role checking and route determination.
 * These helpers are used across all route modules to determine access and navigation.
 */

/**
 * Helper function to check if user has a specific role
 * @param {Object} user - User object
 * @param {string} role - Role to check for
 * @returns {boolean} True if user has the role
 */
export const userHasRole = (user, role) => {
  if (!user) return false;
  
  // Check roles array first
  if (user.roles && Array.isArray(user.roles) && user.roles.includes(role)) {
    return true;
  }
  
  // Check single role field
  if (user.role === role) {
    return true;
  }
  
  // Extra safety for demo users - check character_type if roles are missing/misconfigured
  if (user.demo_mode && user.character_type) {
    if (role === 'admin' && ['admin', 'admin_provider', 'clinical_director'].includes(user.character_type)) return true;
    if (role === 'clinical_director' && user.character_type === 'clinical_director') return true;
    if (role === 'client' && ['client', 'parent_of_client', 'part_of_couple'].includes(user.character_type)) return true;
    if (role === 'provider' && ['therapist', 'student_therapist', 'supervisor', 'psychiatric_np'].includes(user.character_type)) return true;
    // Map supervisor character_type to supervisor role for supervision features
    if (role === 'supervisor' && user.character_type === 'supervisor') return true;
    // Map student_therapist character_type to supervised_provider role for supervision features
    if (role === 'supervised_provider' && user.character_type === 'student_therapist') return true;
  }
  
  return false;
};

/**
 * Helper function to check if user has an admin-level role (admin or clinical_director).
 * Use this instead of checking for admin and clinical_director separately to ensure
 * clinical_director always has parity with admin across all route guards.
 * @param {Object} user - User object
 * @returns {boolean} True if user has admin or clinical_director role
 */
export const isAdminRole = (user) => {
  if (!user) return false;
  return userHasRole(user, 'admin') || userHasRole(user, 'clinical_director');
};

/**
 * Helper function to check if user is a client
 * @param {Object} user - User object
 * @returns {boolean} True if user is a client
 */
export const isClientUser = (user) => {
  return user && ((user.demo_mode && ['client','parent_of_client','part_of_couple'].includes(user.character_type)) || (!user.demo_mode && userHasRole(user, 'client')));
};

/**
 * Pure read: check if a demo character switch is in progress for the given
 * route WITHOUT clearing the flags.  Safe to call during render.
 *
 * @param {string} targetRoute - The route path to check (e.g. '/dashboard')
 * @param {string[]} allowedRoles - Character types that should land on this route
 * @returns {boolean}
 */
export const peekDemoBypass = (targetRoute, allowedRoles) => {
  try {
    const pending = sessionStorage.getItem('pending_demo_target');
    if (pending && pending === targetRoute) return true;
    const forced = sessionStorage.getItem('force_demo_role');
    if (forced && allowedRoles.includes(forced)) return true;
  } catch (_) { void 0; }
  return false;
};

/**
 * Clear the demo-bypass sessionStorage flags.  Call this inside a
 * useEffect (or other committed side-effect) after the bypass page has
 * mounted so that the flags are not consumed during the render phase.
 */
export const consumeDemoBypass = () => {
  try {
    sessionStorage.removeItem('pending_demo_target');
    sessionStorage.removeItem('force_demo_role');
  } catch (_) { void 0; }
};

/**
 * Wrapper that clears demo-bypass flags after the child page mounts.
 * Use this around any component rendered via a peekDemoBypass() guard
 * so the sessionStorage mutation happens in a committed effect, not
 * during render.
 */
export function DemoBypassGuard({ children }) {
  useEffect(() => { consumeDemoBypass(); }, []);
  return children;
}

/**
 * Determine landing page for user.
 * Pure read — no sessionStorage side effects.  Uses peekDemoBypass-style
 * reads so it is safe to call inside JSX / Navigate expressions.
 * consumeDemoBypass() (via DemoBypassGuard) handles cleanup after mount.
 *
 * @param {Object} u - User object
 * @returns {string} Route path for user's landing page
 */
export const landingFor = (u) => {
  try {
    const pending = sessionStorage.getItem('pending_demo_target');
    if (pending) return pending;
    const forced = sessionStorage.getItem('force_demo_role');
    if (forced) {
      return forced === 'admin'
        ? '/admin'
        : (['client','parent_of_client','part_of_couple'].includes(forced) ? '/client' : '/dashboard');
    }
  } catch (_) { void 0; }
  if (!u) return '/login';

  // Users who haven't completed signup consent must go there first.
  // Demo users are exempt.
  if (!u.demo_mode && u.signup_consent_completed === false) {
    return '/signup-consent';
  }

  const isDemo = !!u.demo_mode;
  const character = u.character_type;
  if (isDemo) {
    if (['client','parent_of_client','part_of_couple'].includes(character)) return '/client';
    if (character === 'admin') return '/admin';
    return '/dashboard';
  }
  if (userHasRole(u, 'platform_owner')) return '/platform-owner';
  if (u.role === 'client') return '/client';
  if (isAdminRole(u)) return '/admin';
  return '/dashboard';
};
